import { ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useFormState } from 'react-hook-form';
import { Prompt } from 'react-router';
import { Link, useLocation } from 'react-router-dom-v5-compat';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ApiAttributesBlocklist from 'dashboard/components/ApiAttributesBlocklist';
import AttributeItem from 'dashboard/components/AttributeItem';
import CopyableField from 'dashboard/components/CopyableField';
import NavBack from 'dashboard/components/NavBack';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import DismissibleBanner from 'dashboard/components-lib/DismissibleBanner/DismissibleBanner';
import { FormExpireApiKey, FormExpireApiKeyData } from 'dashboard/forms/FormExpireApiKey';
import {
  FormPasswordConfirmationModal,
  FormPasswordConfirmationModalData,
} from 'dashboard/forms/FormPasswordConfirmationModal';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  Backdrop,
  ColumnContainer,
  InfoGrid,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { ApiKey } from 'dashboard/models/api-key';
import { Organization } from 'dashboard/models/organization';
import { FormUpdateApiKeyData } from 'dashboard/routes/DashboardApiKeysShowRoute/DashboardApiKeysShowRoute';
import ApiPermissionsTable from 'dashboard/views/DashboardApiKeysShowView/ApiPermissionsTable';
import IpAddressAllowlist from 'dashboard/views/DashboardApiKeysShowView/IpAddressAllowlist';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import {
  API_KEY_INFLECTION_LABEL_MAP,
  ApiKeyInflection,
  ApiVersion,
  LATEST_API_VERSION,
} from 'lib/constants/api';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

const HeaderRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DangerParagraph = styled(Paragraph)`
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

interface DashboardApiKeysShowViewProps {
  organization: Organization;
  apiKey: ApiKey;
  handleUpdateApiKey: (
    apiKey: ApiKey,
    data: FormUpdateApiKeyData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleExpireApiKey: (
    apiKey: ApiKey,
    data: FormExpireApiKeyData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  isSuccess: boolean;
}

const DashboardApiKeysShowView = ({
  organization,
  apiKey,
  handleUpdateApiKey,
  handleExpireApiKey,
  isSuccess,
}: DashboardApiKeysShowViewProps) => {
  // Do not require password confirmation if Organization has SAML enabled
  const requirePasswordConfirmation = !organization.attributes['has-saml-enabled'];

  const [
    passwordConfirmationCallback,
    // NOTE: Because this is a function state, need to "wrap" the function call
    // i.e. () => () => {}
    //
    // Reference: https://stackoverflow.com/questions/55621212/is-it-possible-to-react-usestate-in-react
    setPasswordConfirmationCallback,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ] = useState<(meta: FormPasswordConfirmationModalData) => void>(null);

  const [
    isOpenPasswordConfirmationModal,
    { enable: showPasswordConfirmationModal, disable: hidePasswordConfirmationModal },
  ] = useToggle(false);

  const [
    isOpenExpireApiKeyModal,
    { enable: showExpireApiKeyModal, disable: hideExpireApiKeyModal },
  ] = useToggle(false);

  const {
    register,
    trigger,
    handleSubmit: handleReactHookFormSubmit,
    control,
    reset,
  } = useFormSecurely<FormUpdateApiKeyData>({
    mode: 'onBlur',
    shouldUnregister: true,
  });

  const { isSubmitting, isDirty, errors, dirtyFields } = useFormState({
    control,
  });
  const disabled = isSubmitting;

  useEffect(() => {
    // Reset default values whenever API Key updates
    reset({
      name: apiKey.attributes['name'],
      note: apiKey.attributes['note'],
      'api-version': apiKey.attributes['api-version'],
      'api-key-inflection': apiKey.attributes['api-key-inflection'],
      'api-attributes-blocklist': apiKey.attributes['api-attributes-blocklist'],
      permissions: apiKey.attributes['permissions'],
      'ip-address-allowlist': apiKey.attributes['ip-address-allowlist'],
      'file-access-token-expires-in': apiKey.attributes['file-access-token-expires-in'],
    });
  }, [apiKey, reset]);

  const location = useLocation();
  useEffect(() => {
    // Reset default values whenever tab changes
    reset();
  }, [location.pathname, reset]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const canUpgradeApiVersion = apiKey.attributes['api-version'] < LATEST_API_VERSION;
  const apiVersionOptions = Object.values(ApiVersion)
    // don't let user downgrade version
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    .filter((v) => v >= apiKey.attributes['api-version'] && v <= LATEST_API_VERSION)
    .sort()
    .reverse()
    .map((v) => {
      return v === LATEST_API_VERSION ? (
        // mark latest version
        <Option value={v}>{v}(Latest)</Option>
      ) : (
        <Option value={v}>{v}</Option>
      );
    });

  return (
    <>
      <Helmet title={`${apiKey.attributes.name} - API keys`} />
      <Header title={<NavBack to='/dashboard/api-keys'>All API keys</NavBack>}>
        <HeaderRow>
          <HStack>
            <Button
              variant='primary'
              id='api-key__header__save'
              disabled={disabled || !isDirty}
              onClick={async () => {
                const result = await trigger();
                if (result) {
                  if (requirePasswordConfirmation) {
                    setPasswordConfirmationCallback(
                      () => (meta: FormPasswordConfirmationModalData) => {
                        handleReactHookFormSubmit(async (data: FormUpdateApiKeyData) => {
                          await handleUpdateApiKey(apiKey, data, meta);
                        })();
                      },
                    );
                    showPasswordConfirmationModal();
                  } else {
                    await handleReactHookFormSubmit(async (data: FormUpdateApiKeyData) => {
                      await handleUpdateApiKey(apiKey, data);
                    })();
                  }
                }
              }}
            >
              Save
            </Button>

            <Button
              variant='basic'
              id='api-key__header__expire'
              onClick={async () => {
                showExpireApiKeyModal();
              }}
            >
              Expire
            </Button>
          </HStack>
        </HeaderRow>
      </Header>
      {isSuccess && (
        <DismissibleBanner localStorageKey={LocalStorageKey.APIEditInfoBanner}>
          <Paragraph>
            <span>Congrats on your newly updated API key! </span>
            <a
              href='https://docs.withpersona.com/reference/list-all-api-keys'
              target='_blank'
              rel='noreferrer'
            >
              Test API keys through API reference <ArrowRightIcon size='14' />
            </a>
          </Paragraph>
        </DismissibleBanner>
      )}
      <Backdrop>
        <Container>
          <Prompt
            when={Object.keys(dirtyFields).length > 0 && !isSubmitting} // https://github.com/react-hook-form/react-hook-form/issues/3213
            message='You have unsaved changes. Are you sure you want to leave?'
          />{' '}
          <ColumnContainer>
            <MainColumn>
              <RoutedTabbedSection
                tabs={[
                  {
                    name: 'Overview',
                    path: '',
                    contents: (
                      <div key='api-key-overview'>
                        <Card>
                          <Card.Body>
                            <Card.Title>API key</Card.Title>
                            <CopyableField
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              value={apiKey.attributes['value']}
                              type='password'
                              id='api-key__tabs--overview__value'
                            />
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>API version</Card.Title>
                            <Paragraph color='primary'>
                              Currently, your requests default to use API version{' '}
                              <strong>{apiKey.attributes['api-version']}</strong>.
                            </Paragraph>

                            {canUpgradeApiVersion && (
                              <Controller
                                name='api-version'
                                control={control}
                                render={({ field }) => (
                                  <Select {...field}>{apiVersionOptions}</Select>
                                )}
                              />
                            )}
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>Key inflection</Card.Title>
                            <Paragraph color='primary'>
                              Configure the member name formatting for all API responses.{' '}
                              <a
                                href='https://docs.withpersona.com/reference/key-inflection'
                                target='_blank'
                                rel='noreferrer'
                              >
                                Learn more
                              </a>
                              .
                            </Paragraph>

                            <Controller
                              name='api-key-inflection'
                              control={control}
                              render={({ field }) => (
                                <Select {...field}>
                                  <Option value={ApiKeyInflection.Kebab}>
                                    {API_KEY_INFLECTION_LABEL_MAP[ApiKeyInflection.Kebab]}
                                  </Option>
                                  <Option value={ApiKeyInflection.Camel}>
                                    {API_KEY_INFLECTION_LABEL_MAP[ApiKeyInflection.Camel]}
                                  </Option>
                                  <Option value={ApiKeyInflection.Snake}>
                                    {API_KEY_INFLECTION_LABEL_MAP[ApiKeyInflection.Snake]}
                                  </Option>
                                </Select>
                              )}
                            />
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <Card.Title>File access token expiry (seconds)</Card.Title>
                            <Paragraph color='primary'>
                              Configure how long file URLs returned via API request are valid.{' '}
                              <a
                                href='https://docs.withpersona.com/reference/downloading-files'
                                target='_blank'
                                rel='noreferrer'
                              >
                                Learn more
                              </a>
                              .
                            </Paragraph>

                            <Controller
                              name='file-access-token-expires-in'
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <Input
                                  role='input'
                                  type='number'
                                  placeholder="Number of seconds for a file's access token to be valid"
                                  id='file-access-token-expires-in__input'
                                  value={value}
                                  onChange={(e) => {
                                    onChange(e.target.value);
                                  }}
                                />
                              )}
                            />
                          </Card.Body>
                        </Card>

                        {apiKey.attributes['rate-limit'] && (
                          <Card>
                            <Card.Body>
                              <Card.Title>Rate limit</Card.Title>
                              <Paragraph color='secondary'>
                                API requests made via this key are not counted toward the overall
                                environment rate limit. Instead they are limited by the following
                                rate limit. If you need to change this rate limit, please{' '}
                                <Link to='/dashboard/contact-us'>contact us</Link>.
                              </Paragraph>
                              <Spacer height={2} />
                              <Paragraph color='primary'>
                                {apiKey.attributes['rate-limit'].limit} requests per{' '}
                                {apiKey.attributes['rate-limit']['period-seconds']} seconds
                              </Paragraph>
                            </Card.Body>
                          </Card>
                        )}
                      </div>
                    ),
                  },
                  {
                    name: 'API attribute blocklist',
                    path: '/api-attribute-blocklist',
                    contents: (
                      <Card key='api-attribute-blocklist'>
                        <Card.Body>
                          <Card.Title>API attribute blocklist</Card.Title>
                          <Paragraph color='primary'>
                            Blocking API attributes will filter the attributes via API. Wildcard
                            characters (*) can be used.
                          </Paragraph>
                          <Spacer height={1} />
                          <Paragraph color='primary'>
                            Read more about blocked attributes{' '}
                            <a
                              href='https://docs.withpersona.com/reference/api-attribute-blocklists'
                              target='_blank'
                              rel='noreferrer'
                            >
                              here
                            </a>
                            .
                          </Paragraph>

                          <Controller
                            control={control}
                            name='api-attributes-blocklist'
                            render={({ field: { onChange, onBlur, value } }) => (
                              <ApiAttributesBlocklist
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                disabled={disabled}
                                error={errors['api-attributes-blocklist']?.message}
                              />
                            )}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                  {
                    name: 'IP allowlist',
                    path: '/ip-allowlist',
                    contents: (
                      <Card key='ip-allowlist'>
                        <Card.Body>
                          <Card.Title>IP address allowlist</Card.Title>
                          <Paragraph color='primary'>
                            By default, all authenticated traffic is allowed. Use the IP allowlist
                            to limit access.{' '}
                            <a
                              href='https://docs.withpersona.com/docs/security#section-domains-and-ip-addresses'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Learn more
                            </a>
                          </Paragraph>

                          <Controller
                            control={control}
                            name='ip-address-allowlist'
                            render={({ field: { onChange, onBlur, value } }) => (
                              <IpAddressAllowlist
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                disabled={disabled}
                                error={errors['ip-address-allowlist']?.message}
                              />
                            )}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                  {
                    name: 'Permissions',
                    path: '/permissions',
                    contents: (
                      <Card key='permissions'>
                        <Card.Body>
                          <Card.Title>Permissions</Card.Title>
                          <Paragraph color='primary'>
                            For added security, permissions can be set per API key.{' '}
                            <a
                              href='https://docs.withpersona.com/reference/api-keys#permissions'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Learn more
                            </a>
                          </Paragraph>

                          <Controller
                            control={control}
                            name='permissions'
                            render={({ field: { onChange, value } }) => {
                              if (value)
                                return (
                                  <ApiPermissionsTable
                                    value={value}
                                    disabled={disabled}
                                    onChange={onChange}
                                  />
                                );
                              return <>Loading...</>;
                            }}
                          />
                        </Card.Body>
                      </Card>
                    ),
                  },
                ]}
              />
            </MainColumn>

            <RightColumn>
              <Card>
                <Card.Body>
                  <Card.Title>Info</Card.Title>

                  <InfoGrid>
                    <AttributeItem
                      title='Name'
                      value={
                        <>
                          <Input
                            {...register('name', {
                              required: 'Please enter an API key name',
                            })}
                            id='api-key__info__input--name'
                            type='text'
                            placeholder='Name'
                            hasError={!!errors['name']}
                            disabled={disabled}
                          />
                          {errors['name'] && <FormError>{errors['name'].message}</FormError>}
                        </>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Note'
                      value={
                        <>
                          <TextArea
                            {...register('note')}
                            id='api-key__info__textarea--note'
                            disabled={disabled}
                          />
                        </>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Created at'
                      value={
                        apiKey.attributes['created-at'] &&
                        DateTime.fromISO(apiKey.attributes['created-at']).toFormat(
                          DATE_AND_TIME_TZ_FORMAT,
                        )
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Last used at'
                      value={
                        apiKey.attributes['last-used-at'] &&
                        DateTime.fromISO(apiKey.attributes['last-used-at']).toFormat(
                          DATE_AND_TIME_TZ_FORMAT,
                        )
                      }
                      width='100%'
                    />

                    {apiKey.attributes['expires-at'] && (
                      <AttributeItem
                        title='Expires at'
                        value={
                          apiKey.attributes['expires-at'] && (
                            <DangerParagraph>
                              {DateTime.fromISO(apiKey.attributes['expires-at']).toFormat(
                                DATE_AND_TIME_TZ_FORMAT,
                              )}
                            </DangerParagraph>
                          )
                        }
                        width='100%'
                      />
                    )}
                  </InfoGrid>
                </Card.Body>
              </Card>
            </RightColumn>
          </ColumnContainer>
          <FormExpireApiKey
            apiKey={apiKey}
            isOpen={isOpenExpireApiKeyModal}
            handleSubmit={(apiKey: ApiKey, data: FormExpireApiKeyData) => {
              if (requirePasswordConfirmation) {
                setPasswordConfirmationCallback(() => (meta: FormPasswordConfirmationModalData) => {
                  handleExpireApiKey(apiKey, data, meta);
                });
                showPasswordConfirmationModal();
              } else {
                handleExpireApiKey(apiKey, data);
              }
            }}
            handleCloseModal={hideExpireApiKeyModal}
          />
          <FormPasswordConfirmationModal
            isOpen={isOpenPasswordConfirmationModal}
            handleSubmit={passwordConfirmationCallback}
            handleCloseModal={hidePasswordConfirmationModal}
          />
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardApiKeysShowView;
